import React from 'react';
import { Clock, User, Settings, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useActivityLogs } from '@/hooks/useActivityLogs';
import { ACTOR_TYPE_LABELS } from '@/types/activity-logs';
import { format } from 'date-fns';
interface ActivityTimelineProps {
  bookingId: string;
}
const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  bookingId
}) => {
  const {
    activityLogs,
    isLoading,
    error
  } = useActivityLogs(bookingId);
  const getActorIcon = (actorType: string) => {
    switch (actorType) {
      case 'customer':
        return <User className="h-4 w-4" />;
      case 'internal':
        return <Users className="h-4 w-4" />;
      case 'system':
        return <Settings className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };
  const getActorBadgeVariant = (actorType: string) => {
    switch (actorType) {
      case 'customer':
        return 'secondary';
      case 'internal':
        return 'default';
      case 'system':
        return 'outline';
      default:
        return 'secondary';
    }
  };
  if (isLoading) {
    return <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="flex gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>;
  }
  if (error) {
    return <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Failed to load activity timeline</p>
        </CardContent>
      </Card>;
  }
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Activity Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activityLogs.length === 0 ? <p className="text-muted-foreground">No activity recorded yet</p> : <div className="space-y-4">
            {activityLogs.map((log, index) => <div key={log.id} className="flex gap-2">
                <div className="flex flex-col items-center">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full ${log.actor_type === 'customer' ? 'bg-blue-100 text-blue-600' : log.actor_type === 'internal' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                    {getActorIcon(log.actor_type)}
                  </div>
                  {index < activityLogs.length - 1 && <div className="w-px h-8 bg-border mt-2" />}
                </div>
                
                <div className="flex-1 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{log.description}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant={getActorBadgeVariant(log.actor_type)} className="text-xs">
                          {ACTOR_TYPE_LABELS[log.actor_type]}
                        </Badge>
                        {log.actor_name && <span>by {log.actor_name}</span>}
                        {log.actor_email && <span>({log.actor_email})</span>}
                      </div>
                      
                      {/* Display metadata if present */}
                      {log.metadata && Object.keys(log.metadata).length > 0 && <div className="mt-2 text-xs text-muted-foreground">
                          {Object.entries(log.metadata).map(([key, value]) => <div key={key} className="flex gap-1">
                              <span className="font-medium">{key.replace(/_/g, ' ')}:</span>
                              <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                            </div>)}
                        </div>}
                    </div>
                    
                    <time className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(log.created_at), 'MMM d, HH:mm')}
                    </time>
                  </div>
                </div>
              </div>)}
          </div>}
      </CardContent>
    </Card>;
};
export default ActivityTimeline;